import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import axiosInstance from "../../hooks/axios";

type FleetTab = "standard" | "executive";
type DayKey = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";

type GridRow = {
  vehicleTypeId: string;
  description: string;
  seatCapacity: number | null;
  imageName: string | null;
  mon: number;
  tue: number;
  wed: number;
  thu: number;
  fri: number;
  sat: number;
  sun: number;
  syncWithMonday: boolean;
};

type RowState = GridRow & { syncWithMonday: boolean };

const DAYS: { key: DayKey; label: string }[] = [
  { key: "mon", label: "Mon" },
  { key: "tue", label: "Tue" },
  { key: "wed", label: "Wed" },
  { key: "thu", label: "Thu" },
  { key: "fri", label: "Fri" },
  { key: "sat", label: "Sat" },
  { key: "sun", label: "Sun" },
];

function vehicleLabel(row: GridRow): string {
  const d = row.description.toLowerCase();
  const cap = row.seatCapacity;
  if (cap != null && cap <= 4 && d.includes("estate")) return "1-4 PASSENGERS (ESTATE)";
  if (cap != null && cap <= 4) return "1-4 PASSENGERS";
  if (cap != null && cap <= 6) return "6 PASSENGERS";
  if (cap === 7) return "7 PASSENGERS";
  if (cap === 8) return "8 PASSENGERS";
  if (cap === 9) return "9 PASSENGERS";
  if (cap != null && cap <= 14) return "10-14 PASSENGERS";
  if (cap != null && cap >= 15) return "15-16 PASSENGERS";
  return row.description.toUpperCase();
}

export default function NumberOfVehicles() {
  const [fleetTab, setFleetTab] = useState<FleetTab>("standard");
  const [rows, setRows] = useState<RowState[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get<GridRow[]>("/vehicle-availability", {
        params: { fleetCategory: fleetTab, fuelCategory: "petrol_diesel_hybrid" },
      });
      setRows(res.data.map((r) => ({ ...r, syncWithMonday: r.syncWithMonday ?? false })));
    } catch {
      setMessage("Failed to load vehicle availability");
    } finally {
      setLoading(false);
    }
  }, [fleetTab]);

  useEffect(() => {
    load();
  }, [load]);

  const updateDay = (vehicleTypeId: string, day: DayKey, value: number) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.vehicleTypeId !== vehicleTypeId) return r;
        const next = { ...r, [day]: Math.max(0, value) };
        if (r.syncWithMonday && day === "mon") {
          for (const d of DAYS) {
            (next as Record<DayKey, number>)[d.key] = Math.max(0, value);
          }
        }
        return next;
      }),
    );
  };

  const toggleSync = (vehicleTypeId: string, checked: boolean) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.vehicleTypeId !== vehicleTypeId) return r;
        if (!checked) return { ...r, syncWithMonday: false };
        const mon = r.mon;
        return {
          ...r,
          syncWithMonday: true,
          tue: mon,
          wed: mon,
          thu: mon,
          fri: mon,
          sat: mon,
          sun: mon,
        };
      }),
    );
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage("");
    try {
      await axiosInstance.put("/vehicle-availability", {
        fleetCategory: fleetTab,
        fuelCategory: "petrol_diesel_hybrid",
        rows: rows.map((r) => ({
          vehicleTypeId: r.vehicleTypeId,
          mon: r.mon,
          tue: r.tue,
          wed: r.wed,
          thu: r.thu,
          fri: r.fri,
          sat: r.sat,
          sun: r.sun,
          syncWithMonday: r.syncWithMonday,
        })),
      });
      setMessage("Availability saved");
      await load();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string | string[] } } })?.response
          ?.data?.message ?? "Save failed";
      setMessage(Array.isArray(msg) ? msg.join(", ") : String(msg));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-6xl">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <PageBreadcrumb pageTitle="Number of Vehicles" />
        <div className="flex gap-3 text-sm">
          <Link to="/vehicle-type" className="text-brand-600 hover:text-brand-700 hover:underline dark:text-brand-400">
            Add Fleet Type
          </Link>
          <span className="text-gray-300">|</span>
          <Link to="/service-pauses" className="text-brand-600 hover:text-brand-700 hover:underline dark:text-brand-400">
            Scheduled pause
          </Link>
          <span className="text-gray-300">|</span>
          <a
            href="https://www.gov.uk/find-postcode"
            target="_blank"
            rel="noreferrer"
            className="text-brand-600 hover:text-brand-700 hover:underline dark:text-brand-400"
          >
            Help
          </a>
        </div>
      </div>

      <p className="mt-4 text-sm text-gray-600 max-w-4xl">
        How many cars can you simultaneously commit for bookings? Website quotes are limited by
        these concurrent booking numbers. Changing availability does not cancel existing bookings.
        You may not set every vehicle to zero on the same day — use{" "}
        <Link to="/service-pauses" className="text-brand-600 hover:text-brand-700 hover:underline dark:text-brand-400">
          Pause Availability
        </Link>{" "}
        if you are fully closed.
      </p>

      <div className="mt-4 flex gap-2 border-b">
        <button
          type="button"
          className="px-4 py-2 text-sm font-medium border-b-2 -mb-px border-brand-500 text-brand-600 dark:text-brand-400"
        >
          Petrol, Diesel &amp; Hybrid
        </button>
      </div>

      <div className="mt-2 flex gap-2 border-b">
        {(["standard", "executive"] as FleetTab[]).map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setFleetTab(tab)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px capitalize ${
              fleetTab === tab
                ? "border-brand-500 text-brand-600 dark:text-brand-400"
                : "border-transparent text-gray-500"
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="mt-8 text-gray-500">Loading...</p>
      ) : (
        <div className="mt-6 space-y-4">
          {rows.map((row) => (
            <div
              key={row.vehicleTypeId}
              className="rounded-2xl border border-gray-200 bg-white shadow-theme-sm dark:border-gray-800 dark:bg-white/[0.03] p-4 flex flex-wrap items-center gap-4"
            >
              <div className="w-48 shrink-0">
                <p className="text-xs font-semibold text-gray-800 leading-snug">
                  {vehicleLabel(row)}
                </p>
              </div>

              <div className="flex flex-wrap gap-2 flex-1 min-w-[280px]">
                {DAYS.map(({ key, label }) => (
                  <div key={key} className="text-center">
                    <label className="block text-xs text-gray-500 mb-1">{label}</label>
                    <input
                      type="number"
                      min={0}
                      step={1}
                      className="w-12 border rounded px-1 py-1.5 text-sm text-center"
                      value={row[key]}
                      disabled={row.syncWithMonday && key !== "mon"}
                      onChange={(e) =>
                        updateDay(row.vehicleTypeId, key, Number(e.target.value) || 0)
                      }
                    />
                  </div>
                ))}
              </div>

              <label className="flex items-start gap-2 text-xs text-gray-600 max-w-[200px] shrink-0">
                <input
                  type="checkbox"
                  checked={row.syncWithMonday}
                  onChange={(e) => toggleSync(row.vehicleTypeId, e.target.checked)}
                  className="mt-0.5"
                />
                <span>Same no. of vehicles every day, based on Monday&apos;s value</span>
              </label>
            </div>
          ))}

          <div className="flex items-center gap-4 pt-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center justify-center rounded-lg bg-brand-500 px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save availability"}
            </button>
            {message && (
              <p
                className={`text-sm ${
                  message.includes("Failed") || message.includes("failed")
                    ? "text-red-600"
                    : "text-green-600"
                }`}
              >
                {message}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
